import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, User, AuthRequest, VerifyOTPRequest } from '../types';
import { authApi } from '../services/authApi';

// Auth Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'OTP_SENT' }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  account: null,
  token: null,
  loading: false,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        account: null, // Not used in new system
        token: null, // Not used with cookie-based auth
        loading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'OTP_SENT':
      return {
        ...state,
        loading: false,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...initialState,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Context interface
interface AuthContextType {
  state: AuthState;
  requestOTP: (email: string) => Promise<{ sessionId: string; expiresIn: number }>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on app start
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await authApi.getMe();
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.user,
          },
        });
      } catch (error) {
        // No valid session, user needs to login
        dispatch({ type: 'LOGOUT' });
      }
    };

    checkSession();
  }, []);

  const requestOTP = async (email: string): Promise<{ sessionId: string; expiresIn: number }> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authApi.requestOTP({ email });
      dispatch({ type: 'OTP_SENT' });
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const verifyOTP = async (email: string, otp: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authApi.verifyOTP({ email, otp });
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify OTP';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    state,
    requestOTP,
    verifyOTP,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
